import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Gauge, Thermometer, Wind, Ruler } from "lucide-react";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";

import { FaWhatsapp } from "react-icons/fa";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Logo from "../assets/Logo.png";

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [otherProducts, setOtherProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    axios
      .get(`https://api.microwellindustries.com/api/products/slug/${slug}`)
      .then((res) => {
        if (res.data.status !== "Active") {
          toast.error("This product is not available", { autoClose: 2000 });
          setTimeout(() => navigate("/products"), 2200); // wait for toast before redirect
        } else {
          setProduct(res.data);
        }
      })
      .catch((err) => console.error("Error fetching product:", err));
  }, [slug, navigate]);

  // ✅ Fetch only ACTIVE other products
  useEffect(() => {
    axios
      .get(`https://api.microwellindustries.com/api/products`)
      .then((res) => {
        const others = res.data.filter(
          (p) => p.slug !== slug && p.status === "Active"
        );
        const shuffled = others.sort(() => 0.5 - Math.random()).slice(0, 4);
        setOtherProducts(shuffled);
      })
      .catch((err) => console.error("Error fetching products:", err));
  }, [slug]);

  if (!product)
    return (
      <div className="p-10 text-center text-gray-500 text-lg">Loading...</div>
    );

  const whatsappNumber = "917016884684";
  const productPageLink = `${window.location.origin}/product/${slug}`;
  const encodedMessage = encodeURIComponent(
    `Hello, I'm interested in the product: ${product.name}.\n\nProduct Link: ${productPageLink}`
  );
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

  return (
    <div className="bg-white px-4 md:px-8 py-10 max-w-7xl mx-auto">
      {/* Breadcrumb + Title */}
      <motion.div
        className="mb-14"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <nav className="text-sm text-gray-500 mb-4">
          <Link to="/" className="hover:text-blue-600 transition">
            Home
          </Link>{" "}
          /{" "}
          <Link to="/products" className="hover:text-blue-600 transition">
            Products
          </Link>{" "}
          / <span className="text-gray-800 font-semibold">{product.name}</span>
        </nav>
        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
          {product.name}
        </h1>
        <h1 className="text-lg md:text-lg text-blue-700 mb-3">
          TYPE : {product.type}
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed text-justify bg-gray-50 p-6 rounded-xl shadow-sm border w-full">
          {product.application}
        </p>
      </motion.div>

      {/* Product Image(s) + Operating Limits */}
      <motion.div
        className="grid lg:grid-cols-2 gap-12 mb-20 items-center"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        {/* Product Image Card with Triangle Background */}
        <div className="flex justify-center">
          <motion.div
            className="relative rounded-2xl shadow-lg border w-full max-w-md flex items-center justify-center overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Background with Blue Triangle (top-right) */}
            <div className="absolute inset-0 bg-white">
              <div
                className="absolute inset-0"
                style={{
                  clipPath: "polygon(0% 100%, 0% 40%, 120% 100%)",
                  backgroundColor: "#1e3a8a", // Tailwind blue-900
                }}
              ></div>
            </div>

            {/* Watermark top-left (smaller size) */}
            <img
              src={Logo}
              alt="Falcon Sealing Technology"
              className="absolute top-3 left-3 w-28 opacity-90"
            />

            {/* Product Image */}
            <img
              src={product.images?.[0] || product.image || "/placeholder.jpg"}
              alt={product.name}
              className="relative z-10 w-full h-80 object-contain p-6"
            />
          </motion.div>
        </div>

        {/* Operating Limits */}
        {(product.operatingLimits ||
          product.operatingLimits?.shaftDia ||
          product.operatingLimits?.pressure ||
          product.operatingLimits?.temperature ||
          product.operatingLimits?.speed ||
          product.operatingLimits?.tsPressureLimit20 ||
          product.operatingLimits?.tsPressureLimit40) && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Operating Limits
            </h2>
            <div className="space-y-4">
              {product.operatingLimits?.shaftDia && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Ruler className="w-5 h-5 text-blue-600" />
                  <span>
                    <strong>Shaft Dia:</strong>{" "}
                    {product.operatingLimits.shaftDia}
                  </span>
                </div>
              )}
              {product.operatingLimits?.pressure && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Gauge className="w-5 h-5 text-indigo-600" />
                  <span>
                    <strong>Pressure:</strong>{" "}
                    {product.operatingLimits.pressure}
                  </span>
                </div>
              )}
              {product.operatingLimits?.temperature && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Thermometer className="w-5 h-5 text-red-500" />
                  <span>
                    <strong>Temperature:</strong>{" "}
                    {product.operatingLimits.temperature}
                  </span>
                </div>
              )}
              {product.operatingLimits?.speed && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Wind className="w-5 h-5 text-green-600" />
                  <span>
                    <strong>Speed:</strong> {product.operatingLimits.speed}
                  </span>
                </div>
              )}
              {product.operatingLimits?.tsPressureLimit20 && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Gauge className="w-5 h-5 text-blue-600" />
                  <span>
                    <strong>TS 20 Pressure Limit:</strong> {product.operatingLimits.tsPressureLimit20}
                  </span>
                </div>
              )}
              {product.operatingLimits?.tsPressureLimit40 && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Gauge className="w-5 h-5 text-indigo-600" />
                  <span>
                    <strong>TS 40 Pressure Limit:</strong> {product.operatingLimits.tsPressureLimit40}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Materials of Construction + Second Image */}
      <motion.div
        className="grid lg:grid-cols-2 gap-12 mb-20"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        {/* Materials */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Material of Construction
          </h2>
          <div className="border rounded-lg overflow-hidden">
            <dl className="divide-y divide-gray-200">
              {product.materials?.sealRingFaces && (
                <div className="px-4 py-3 bg-gray-50 flex justify-between">
                  <dt className="font-medium text-gray-700 border-r border-gray-300 pr-4 flex-1">Seal Ring Faces</dt>
                  <dd className="text-gray-600 pl-4 flex-1">
                    {product.materials.sealRingFaces}
                  </dd>
                </div>
              )}
              {product.materials?.seatFaces && (
                <div className="px-4 py-3 flex justify-between">
                  <dt className="font-medium text-gray-700 border-r border-gray-300 pr-4 flex-1">Seat Faces</dt>
                  <dd className="text-gray-600 pl-4 flex-1">
                    {product.materials.seatFaces}
                  </dd>
                </div>
              )}
              {product.materials?.elastomer && (
                <div className="px-4 py-3 bg-gray-50 flex justify-between">
                  <dt className="font-medium text-gray-700 border-r border-gray-300 pr-4 flex-1">Elastomer</dt>
                  <dd className="text-gray-600 pl-4 flex-1">
                    {product.materials.elastomer}
                  </dd>
                </div>
              )}
              {product.materials?.moc && (
                <div className="px-4 py-3 flex justify-between">
                  <dt className="font-medium text-gray-700 border-r border-gray-300 pr-4 flex-1">MOC</dt>
                  <dd className="text-gray-600 pl-4 flex-1">{product.materials.moc}</dd>
                </div>
              )}
              {product.materials?.bellowMoc && (
                <div className="px-4 py-3 bg-gray-50 flex justify-between">
                  <dt className="font-medium text-gray-700 border-r border-gray-300 pr-4 flex-1">Bellow MOC</dt>
                  <dd className="text-gray-600 pl-4 flex-1">
                    {product.materials.bellowMoc}
                  </dd>
                </div>
              )}
              {product.materials?.endFittingMoc && (
                <div className="px-4 py-3 flex justify-between">
                  <dt className="font-medium text-gray-700 border-r border-gray-300 pr-4 flex-1">
                    End Fitting MOC
                  </dt>
                  <dd className="text-gray-600 pl-4 flex-1">
                    {product.materials.endFittingMoc}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Second Image */}
        {product.image2 && (
          <div className="flex justify-center">
            <img
              src={product.image2}
              alt="Second view"
              loading="lazy"
              className="w-80 h-80 object-contain rounded-lg border shadow-sm"
            />
          </div>
        )}
      </motion.div>

      {/* CTA */}
      <motion.div
        className="p-10 rounded-2xl text-center mb-9"
        style={{
          backgroundColor: "#1e3a8a",
        }}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        <p className="text-2xl font-semibold text-white mb-7">
          Ask for Expert
        </p>
        <div className="flex justify-center gap-6">
          {/* WhatsApp Button */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-full shadow-lg hover:bg-green-600 transition"
          >
            <FaWhatsapp className="text-xl" />
             WhatsApp
          </a>

          {/* Enquiry Button */}
          <Link
            to="/contact"
            className="bg-white text-gray-900 font-semibold px-8 py-3 rounded-full shadow hover:bg-gray-100 transition"
          >
            ✉️ Enquiry
          </Link>
        </div>
      </motion.div>

      {/* Other Products */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-semibold text-gray-900 mb-7">
          Other Products
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {otherProducts.map((product) => (
            <motion.div
              key={product._id}
              whileHover={{ y: -4 }}
              className="flex"
            >
              <Link
                to={`/product/${product.slug}`}
                className="w-full h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 group overflow-hidden flex flex-col"
              >
                {/* Image with hover overlay */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={product.image || "/placeholder.jpg"}
                    alt={product.name}
                    className="w-full h-full object-contain transform group-hover:scale-105 transition duration-500"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center">
                    <span className="text-white text-lg font-semibold">
                      View Details
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-semibold text-blue-600 uppercase">
                      Type : {product.type}
                    </span>
                    <h3 className="text-xl font-bold text-gray-800 mt-2">
                      {product.name}
                    </h3>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ProductPage;
